import { Link } from "react-router";
import { Mark, Panel, PanelHeader } from "@/components/ui";
import { adjustedEbitda, openExceptions } from "@/lib/finance";
import { formatCompactMoney } from "@/lib/format";
import type { Finding, Metric } from "@/lib/types";
import { useCase } from "./case-context";
import { TraceLink } from "./trace";

/**
 * The overview answers six questions in under two minutes: who is borrowing,
 * what they want, what the financial story is, what the largest risks are, what
 * is missing, and what happens next. It is ordered in that sequence, and the
 * next action sits at the end where the reading finishes.
 */
export function CaseOverview() {
  const { case: kase, analysis, evidence, decision } = useCase();
  const exceptions = openExceptions(evidence);
  const missing = evidence.filter((source) => source.status === "missing");
  const unresolved = kase.openQuestions.filter((question) => !question.resolved);

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Panel>
          <PanelHeader
            title="AI credit summary"
            description="Generated from the evidence on this case. Every claim below is traceable."
          />
          <div className="space-y-3 px-4 py-4">
            {kase.summary.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-relaxed text-ink">
                {paragraph}
              </p>
            ))}
          </div>
        </Panel>

        <div className="grid gap-4 sm:grid-cols-2">
          <FindingList title="Key risks" findings={kase.risks} kind="negative" />
          <FindingList title="Strengths" findings={kase.strengths} kind="positive" />
        </div>

        <Panel>
          <PanelHeader
            title="Financial position"
            action={
              <Link
                to={`/cases/${kase.id}/analysis`}
                className="text-xs text-accent underline decoration-accent-line underline-offset-4"
              >
                Full analysis
              </Link>
            }
          />
          <dl className="grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
            {kase.headlineMetrics.map((metric) => (
              <HeadlineMetric key={metric.id} metric={metric} />
            ))}
          </dl>
          <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">
            Adjusted EBITDA of{" "}
            <span className="numeric font-medium text-ink">
              {formatCompactMoney(adjustedEbitda(analysis.bridge))}
            </span>{" "}
            reflects {analysis.bridge.adjustments.length} analyst adjustment
            {analysis.bridge.adjustments.length === 1 ? "" : "s"}.
          </p>
        </Panel>
      </div>

      <div className="space-y-4">
        <Panel>
          <PanelHeader title="What happens next" />
          <div className="px-4 py-4">
            <NextAction
              caseId={kase.id}
              stage={kase.stage}
              missing={missing.length}
              exceptions={exceptions.length}
              decided={decision.outcome !== null}
            />
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Open questions" description={`${unresolved.length} unresolved`} />
          {unresolved.length === 0 ? (
            <p className="px-4 py-4 text-xs text-ink-muted">
              Nothing outstanding. The case is ready to progress.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {unresolved.map((question) => {
                const target =
                  question.target.tab === "overview"
                    ? `/cases/${kase.id}`
                    : `/cases/${kase.id}/${question.target.tab}`;
                const href = question.target.anchor
                  ? `${target}#${question.target.anchor}`
                  : target;

                return (
                  <li key={question.id} className="flex items-start justify-between gap-3 px-4 py-3">
                    <span className="text-xs leading-relaxed text-ink">{question.question}</span>
                    <Link
                      to={href}
                      className="shrink-0 rounded border border-line-strong px-2 py-0.5 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
                    >
                      Review
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel>
          <PanelHeader title="Evidence status" />
          <dl className="divide-y divide-line text-xs">
            <StatusRow label="Sources received" value={`${evidence.filter((s) => s.status === "received").length} of ${evidence.length}`} />
            <StatusRow label="Open exceptions" value={String(exceptions.length)} tone={exceptions.length > 0 ? "caution" : undefined} />
            <StatusRow label="Missing documents" value={String(missing.length)} tone={missing.length > 0 ? "negative" : undefined} />
          </dl>
        </Panel>
      </div>
    </div>
  );
}

function HeadlineMetric({ metric }: { metric: Metric }) {
  return (
    <div className="px-4 py-3">
      <dt className="eyebrow">{metric.label}</dt>
      <dd className="numeric mt-1 text-lg font-semibold text-ink">
        <TraceLink trace={metric.trace}>{metric.value}</TraceLink>
      </dd>
    </div>
  );
}

function FindingList({
  title,
  findings,
  kind,
}: {
  title: string;
  findings: Finding[];
  kind: "positive" | "negative";
}) {
  return (
    <Panel>
      <PanelHeader title={title} />
      <ul className="divide-y divide-line">
        {findings.map((finding) => (
          <li key={finding.id} className="flex items-start gap-2 px-4 py-2.5">
            <Mark kind={kind === "positive" ? "positive" : "caution"} />
            <TraceLink trace={finding.trace} className="text-xs leading-relaxed text-ink">
              {finding.label}
            </TraceLink>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function StatusRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "caution" | "negative";
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <dt className="text-ink-muted">{label}</dt>
      <dd
        className={
          tone === "negative"
            ? "numeric font-medium text-negative"
            : tone === "caution"
              ? "numeric font-medium text-caution"
              : "numeric font-medium text-ink"
        }
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * There is always exactly one next action, and it is a link, not advice. The
 * ordering is deliberate: a missing document blocks everything, an unverified
 * figure blocks a defensible decision, and a clean case goes to Decision.
 */
function NextAction({
  caseId,
  stage,
  missing,
  exceptions,
  decided,
}: {
  caseId: string;
  stage: string;
  missing: number;
  exceptions: number;
  decided: boolean;
}) {
  const action = (() => {
    if (decided) {
      return { label: "Review the recorded decision", href: `/cases/${caseId}/decision`, why: "This case has been decided." };
    }
    if (missing > 0) {
      return {
        label: "Chase missing evidence",
        href: `/cases/${caseId}/evidence`,
        why: `${missing} source${missing === 1 ? " is" : "s are"} outstanding. Underwriting cannot complete without them.`,
      };
    }
    if (stage === "approval") {
      return { label: "Make the credit decision", href: `/cases/${caseId}/decision`, why: "Evidence is complete and all models have run." };
    }
    if (exceptions > 0) {
      return {
        label: "Resolve open exceptions",
        href: `/cases/${caseId}/evidence`,
        why: `${exceptions} extracted value${exceptions === 1 ? "" : "s"} still need confirming before a decision is defensible.`,
      };
    }
    return { label: "Move to decision", href: `/cases/${caseId}/decision`, why: "Nothing is outstanding." };
  })();

  return (
    <>
      <Link
        to={action.href}
        className="block w-full rounded-md bg-accent px-3 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-accent/90"
      >
        {action.label}
      </Link>
      <p className="mt-2 text-xs leading-relaxed text-ink-muted">{action.why}</p>
    </>
  );
}
